import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Client } from '@app/core/models/client';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ClientService } from '@app/core/services/client.service';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { TitleService } from '@app/core/services/title.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { TwoWayConversationService } from '@app/core/services/two-way-conversation.service';
import { map, switchMap, take } from 'rxjs/operators';
import { BlackoutHour } from '@app/core/models/blackout-hour';
import {
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { Languages } from '@app/two-way/twilio-conversation.types';
import { ClientHeaderComponent } from '../../../shared/client-header/client-header.component';
import { ImageInputComponent } from '../../image-input/image-input.component';
import { RadioBtnComponent } from '../../../shared/radio-btn/radio-btn.component';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { CheckboxComponent } from '../../../shared/checkbox/checkbox.component';
import { BlackoutSelectComponent } from '../../../shared/blackout-select/blackout-select.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../../shared/message-dialog/message-dialog.component';

@Component({
  selector: 'app-client-images-new',
  templateUrl: './client-images-new.component.html',
  styleUrls: ['./client-images-new.component.scss'],
  standalone: true,
  imports: [
    ClientHeaderComponent,
    ImageInputComponent,
    RadioBtnComponent,
    FormsModule,
    NgClass,
    ReactiveFormsModule,
    NgFor,
    NgIf,
    CheckboxComponent,
    BlackoutSelectComponent,
    MessageDialogComponent_1,
  ],
})
export class ClientImagesNewComponent implements OnInit, OnDestroy {
  @ViewChild(MessageDialogComponent, { static: true })
  messageDialog: MessageDialogComponent;
  @ViewChild('toggleOnFeedWarning', { static: true })
  toggleOnFeedWarningDialog: MessageDialogComponent;
  @ViewChild('toggleOffFeedWarning', { static: true })
  toggleOffFeedWarningDialog: MessageDialogComponent;
  client: Client;
  clientApiKeyValidGroup: UntypedFormGroup;
  apiKey = null;
  languages: Languages[] = [];
  languageSelections: Record<string, boolean> = {};
  headerGroups = {
    portalImages: true,
    clientID: true,
    twoWayMessaging: true,
    messageBlackoutWindow: true,
  };
  featureFlags = FeatureFlags;
  hours: BlackoutHour[];
  showFDIC: boolean;
  isProvisioningTwoWay = false;
  showValidApiKeyMessage: boolean = false;
  showInvalidApiKeyMessage: boolean = false;
  showFailedLoadApiKeyAndLanguagesMessage: boolean = false;
  showApiKey: boolean = false;
  isLoadingTranslations: boolean = false;
  constructor(
    private activatedRoute: ActivatedRoute,
    private clientService: ClientService,
    private featureService: FeatureService,
    private titleService: TitleService,
    private router: Router,
    private twoWayConversationService: TwoWayConversationService,
  ) {
    this.hours = this.clientService.getBlackoutHours();
    this.client = new Client();
    this.getClient();
  }

  ngOnInit() {
    this.showFDIC = this.featureService.checkFlag('fdic');
    this.initForm();
  }

  ngOnDestroy() {
    this.titleService.deactivate();
  }

  initForm() {
    this.clientApiKeyValidGroup = new UntypedFormGroup({});
    if (this.rmTranslationsWithLanguagesFlagOn) {
      this.clientApiKeyValidGroup.addControl(
        'api_key',
        new UntypedFormControl({ value: this.apiKey, disabled: true }),
      );
      this.clientApiKeyValidGroup.addControl(
        'languages',
        new UntypedFormControl({ value: [], disabled: true }),
      );
    } else if (this.rmTranslationsFeatureFlagOn) {
      this.clientApiKeyValidGroup.addControl(
        'api_key',
        new UntypedFormControl({ value: this.apiKey, disabled: true }),
      );
    }
  }

  updateApiKeyControl(apiKey: string) {
    const control = this.clientApiKeyValidGroup.get('api_key');

    if (control) {
      control.setValue(apiKey);
      control.enable();
    }
  }

  private updateLanguagesControl(
    languages: Languages[],
    notValidApiKey: boolean = false,
  ): void {
    const control = this.clientApiKeyValidGroup?.get('languages');

    if (!control) {
      this.languageSelections = {};
      return;
    }

    const languagesValue = (languages ?? []).map((language) =>
      notValidApiKey ? { ...language, supportedByApiKey: false } : language,
    );

    if (notValidApiKey) {
      this.languages = languagesValue;
    }

    control.setValue(languagesValue);

    if (languagesValue.length) {
      control.enable();
    } else {
      control.disable({ emitEvent: false });
    }

    this.languageSelections = languagesValue.reduce<Record<string, boolean>>(
      (accumulator, language) => {
        accumulator[language.code] = language.enabledInRm;
        return accumulator;
      },
      {},
    );
  }

  toggleHeader(headerGroup: string) {
    if (this.headerGroups[headerGroup] !== undefined) {
      this.headerGroups[headerGroup] = !this.headerGroups[headerGroup];
    }
  }

  /**
   * Returns the client's 'New Feed' setting
   * Defaults to 'false' if not previously set
   */
  get newFeedEnabled(): boolean {
    return this.client.feed_enabled;
  }

  get fdicEnabled(): boolean {
    return this.client.fdic_enabled;
  }

  get RMEnabled(): boolean {
    return this.client.two_way_config.enabled;
  }

  get RMTranslationsEnabled(): boolean {
    return this.client.two_way_config.translations_enabled;
  }

  /**
   * Called when client clicks the 'New Feed' switch or when user click "Cancel" on pop-up warning
   * Toggles 'off' to 'on' and vice versa
   * Case 1 "User clicks toggle" : The toggle switches and a pop-up warning appears to confirm if user want to update
   * Case 2 "User click Cancel on pop-up": the toggle switches back to its prior state before the user clicks the toggle.
   */
  toggleNewFeedEnabled(backToPriorState: boolean = false): void {
    if (backToPriorState) {
      this.client.feed_enabled = !this.client.feed_enabled;
    } else {
      this.client.feed_enabled = !this.client.feed_enabled;
      this.displayWarningToggleNewFeed(this.client.feed_enabled);
    }
  }

  toggleFdicEnabled(): void {
    this.client.fdic_enabled = !this.client.fdic_enabled;
  }

  /**
   * Called when user click Enable/Disable on toggle-feed pop-up
   */
  closeToggleFeedDialog() {
    this.toggleOffFeedWarningDialog.close();
    this.toggleOnFeedWarningDialog.close();
  }

  displayWarningToggleNewFeed(isFeedTurningOn: boolean): void {
    if (isFeedTurningOn) {
      this.toggleOnFeedWarningDialog.showMessage();
    } else {
      this.toggleOffFeedWarningDialog.showMessage();
    }
  }

  /**
   * Determines if the "New Feed" toggle is visible to the user
   */
  get newFeedFeatureFlagOn(): boolean {
    return this.featureService.checkFlag(this.featureFlags.new_feed);
  }

  /**
   * Determines if the "RM Translations" is enabled for the user
   */
  get rmTranslationsFeatureFlagOn(): boolean {
    return this.featureService.checkFlag(this.featureFlags.rm_translations);
  }

  /**
   * Determines if the "RM Translations with Languages" is enabled for the user
   */
  get rmTranslationsWithLanguagesFlagOn(): boolean {
    return this.featureService.checkFlag(this.featureFlags.rm_multi_lingual);
  }

  saveAndContinue(): void {
    if (this.client.two_way_config.translations_enabled) {
      const apiKeyControlValue =
        this.clientApiKeyValidGroup.get('api_key')?.value ?? null;

      this.clientService
        .updateClient(this.client)
        .pipe(
          switchMap((updateClientResult) =>
            this.saveTranslationsConfiguration(apiKeyControlValue).pipe(
              map(() => updateClientResult),
            ),
          ),
        )
        .subscribe(
          (updateClientResult) => {
            this.onSuccessfulSave(updateClientResult);
          },
          (err: HttpErrorResponse) => this.onFailedSaved(err),
        );
    } else {
      this.clientService.updateClient(this.client).subscribe(
        (res) => {
          this.onSuccessfulSave(res);
        },
        (err: HttpErrorResponse) => this.onFailedSaved(err),
      );
    }
  }

  private saveTranslationsConfiguration(apiKey: string): Observable<any> {
    if (this.rmTranslationsWithLanguagesFlagOn) {
      const languagesPayload = (this.languages ?? []).map((language) => ({
        code: language.code,
        enabledInRm: language.enabledInRm,
      }));
      return this.twoWayConversationService.saveConfigsTranslations(
        this.client.id,
        {
          apiKey,
          languages: languagesPayload,
        },
      );
    }

    return this.twoWayConversationService.saveApiKey(
      this.client.id,
      'languageio',
      apiKey,
    );
  }

  validateApiKey(): void {
    this.twoWayConversationService
      .validateApiKey(
        this.client.id,
        'languageio',
        this.clientApiKeyValidGroup.get('api_key')?.value,
      )
      .subscribe(
        (res) => {
          if (res.valid) {
            this.showValidApiKeyMessage = true;
            this.showInvalidApiKeyMessage = false;
          } else {
            this.showValidApiKeyMessage = false;
            this.showInvalidApiKeyMessage = true;
          }
        },
        (err: HttpErrorResponse) => {
          this.showValidApiKeyMessage = false;
          this.showInvalidApiKeyMessage = true;
        },
      );
  }

  private validateApiKeyConfigsTranslations(): void {
    this.isLoadingTranslations = true;
    this.twoWayConversationService
      .validateApiKeyConfigsTranslations(
        this.client.id,
        this.clientApiKeyValidGroup.get('api_key')?.value,
      )
      .subscribe(
        (res) => {
          this.showValidApiKeyMessage = true;
          this.showInvalidApiKeyMessage = false;
          this.showFailedLoadApiKeyAndLanguagesMessage = false;
          this.apiKey = this.clientApiKeyValidGroup.get('api_key')?.value;
          this.updateApiKeyControl(this.apiKey);
          this.languages = res.languages;
          this.updateLanguagesControl(this.languages);
          this.isLoadingTranslations = false;
        },
        (err: HttpErrorResponse) => {
          this.showValidApiKeyMessage = false;
          this.showInvalidApiKeyMessage = true;
          this.showFailedLoadApiKeyAndLanguagesMessage = false;
          this.updateLanguagesControl(this.languages, true);
          this.isLoadingTranslations = false;
        },
      );
  }

  validateApiKeyClick(): void {
    if (this.rmTranslationsWithLanguagesFlagOn) {
      this.validateApiKeyConfigsTranslations();
    } else {
      this.validateApiKey();
    }
  }

  private onSuccessfulSave(res: Client): void {
    const clientId = res.id;
    this.router.navigateByUrl(`/client/${clientId}/edit/validation`);
  }

  private onFailedSaved(err: HttpErrorResponse): void {
    const serverMessage = this.clientService.cleanseError(err);
    const errMsg = err.error?.message || '';
    if (errMsg === 'invalid API key provided') {
      this.showInvalidApiKeyMessage = true;
      this.showValidApiKeyMessage = false;
      this.updateLanguagesControl(this.languages, true);
      this.messageDialog.showMessage(
        `Oops...could not save API Key. Please check the API key provided is valid.`,
      );
    } else if (errMsg === 'failed to save api key and client languages') {
      this.messageDialog.showMessage(
        `Oops...could not save API Key and languages configuration. Please check the API key provided is valid and try again.`,
      );
    } else {
      this.messageDialog.showMessage(
        `Oops...could not save client: ${serverMessage}`,
      );
    }
  }

  previousStep(): void {
    this.router.navigateByUrl(`/client/${this.client.id}/edit`);
  }

  private getClient(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.clientService.getClient(params['clientId']).subscribe(
        (client) => {
          this.client = Client.deserialize(client);

          // Initialize translations_enabled if RM is already enabled
          if (
            this.rmTranslationsFeatureFlagOn &&
            this.newFeedEnabled &&
            this.RMEnabled &&
            this.client.two_way_config.translations_enabled === undefined
          ) {
            this.client.two_way_config.translations_enabled = false;
          }
          if (this.client.two_way_config.translations_enabled) {
            if (this.rmTranslationsWithLanguagesFlagOn) {
              this.getConfigsTranslations();
            } else {
              this.getApiKey();
            }
          }
        },
        (error) => {
          this.messageDialog.showMessage(
            `Oops...Could not load client: ${params['clientId']}`,
          );
        },
        () => {
          this.setPrimaryTitle();
        },
      );
    });
  }

  private getApiKey(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.twoWayConversationService
        .getApiKey(params['clientId'], 'languageio')
        .subscribe(
          (apiKey) => {
            this.apiKey = apiKey;
            this.updateApiKeyControl(this.apiKey);
          },
          (error) => {
            this.apiKey = null;
            this.updateApiKeyControl(this.apiKey);
          },
        );
    });
  }

  private getConfigsTranslations(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.isLoadingTranslations = true;
      this.twoWayConversationService
        .getConfigsTranslations(params['clientId'])
        .subscribe(
          (translations) => {
            this.showFailedLoadApiKeyAndLanguagesMessage = false;
            this.apiKey = translations.apiKey;
            this.updateApiKeyControl(this.apiKey);
            this.languages = translations.languages;
            this.updateLanguagesControl(this.languages);
            this.isLoadingTranslations = false;
          },
          (error) => {
            this.showFailedLoadApiKeyAndLanguagesMessage = true;
            this.apiKey = null;
            this.updateApiKeyControl(this.apiKey);
            this.languages = [];
            this.updateLanguagesControl(this.languages);
            this.isLoadingTranslations = false;
          },
        );
    });
  }

  private setPrimaryTitle(): void {
    this.titleService.activate(
      this.client && this.client.company_name
        ? 'Configure Client - ' + this.client.company_name
        : 'Configure Client',
    );
  }

  toggleTwoWay(): void {
    if (!this.client.two_way_config.enabled) {
      this.toggleTwoWayConversation();
    } else {
      this.client.two_way_config.enabled = !this.client.two_way_config.enabled;
    }
  }

  toggleTwoWayConversation(): void {
    this.isProvisioningTwoWay = true;
    this.twoWayConversationService
      .provisionTwoWay(this.client.id)
      .pipe(take(1))
      .subscribe(
        () => {
          this.client.two_way_config.enabled = true;
          // Initialize translations_enabled if RM was just enabled
          if (
            this.rmTranslationsFeatureFlagOn &&
            this.newFeedEnabled &&
            this.client.two_way_config.translations_enabled === undefined
          ) {
            this.client.two_way_config.translations_enabled = false;
          }
          this.isProvisioningTwoWay = false;
        },
        (err) => {
          this.isProvisioningTwoWay = false;
          this.onFailedSaved(err);
        },
      );
  }

  toggleRMTranslations(): void {
    this.client.two_way_config.translations_enabled =
      !this.client.two_way_config.translations_enabled;
    if (this.client.two_way_config.translations_enabled) {
      if (this.rmTranslationsWithLanguagesFlagOn) {
        this.getConfigsTranslations();
      } else {
        this.getApiKey();
      }
    }
  }

  onLanguageCheckboxChange(languageCode: string, enabled: boolean): void {
    this.languages = this.languages.map((language) =>
      language.code === languageCode
        ? { ...language, enabledInRm: enabled }
        : language,
    );

    this.updateLanguagesControl(this.languages);
  }

  toggleSmartActionsEnabled(): void {
    this.client.smart_actions_enabled = !this.client.smart_actions_enabled;
  }

  togglePowerUpsEnabled(): void {
    this.client.powerups_enabled = !this.client.powerups_enabled;
  }

  toggleContentProEnabled(): void {
    this.client.content_pro_enabled = !this.client.content_pro_enabled;
  }
}
